import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';


import {
  filterItems,
  getInitialFilters,
  getPackageItemsTaxonomy,
  getUpdatedCategoryValues,
  hydrateFilteredItemsWithAds,
  getNewActiveFilters,
} from 'lib/waffleUtils';

import { Header } from 'components/packages/Waffle/Header';
import { List } from 'components/packages/Waffle/List';
import { LayoutContext } from 'lib/ContextTypes/layout';

import styles from '../styles.module.scss';

const subTypesWithMultiselectOn = ['recipeWaffle', 'productWaffle'];

/**
 * Maps Redux state to component props.
 *
 * @param {object} state - The Redux state object.
 * @param {object} state.shared - Shared state slice.
 * @param {object} state.front - Front state slice.
 */
const mapStateToProps = ({ shared, front }) => ({
  vertical: shared.vertical,
  draftId: front.draftId,
});

/**
 * @param {object} props
 * @param {boolean} [props.adsDisabled=false] - If true, disables ads in the card list.
 * @param {import('../../../../lib/graphqlTypeDefs.mjs').Package} [props.content={}] - Content data including items, metadata, and subtype.
 * @param {string} [props.pkgClassName='pkg waffle'] - CSS class for the package container.
 * @param {string} props.draftId - Optional draft identifier for taxonomy fetching.
 */
const DefaultWaffle = ({
  adsDisabled = false,
  content,
  pkgClassName = 'pkg waffle',
  draftId,
}) => {
  const [categories, setCategories] = useState({});
  const [filterValues, setFilterValues] = useState({});
  const [categoryToDisplay, setCategoryToDisplay] = useState({});
  const [activeFilters, setActiveFilters] = useState(new Map());
  const [displayNameByKey, setDisplayNameByKey] = useState({});
  const [idToTaxonomy, setIdToTaxonomy] = useState({});

  const containerRef = useRef(null);
  const { pageRoute, vertical } = useContext(LayoutContext);

  const {
    id,
    items,
    metadata,
    subType,
  } = content || {};

  const {
    mobileDisplay,
    showCommerceDisclaimer = false,
    multiSelect: enableMultiselectFilters = subTypesWithMultiselectOn.includes(subType),
    customFilters,
  } = metadata || {};

  const filterStates = Object.values(filterValues).map((filterValue) => ({
    type: 'taxonomy',
    values: filterValue,
  }));

  const waffleCards = (items || []).filter((item) => item);

  const cardsWithoutAds = waffleCards.filter(({ type }) => type !== 'ad');

  const filteredCards = filterItems(cardsWithoutAds, filterStates, idToTaxonomy); // cards with filters applied

  const cards = adsDisabled
    ? filteredCards
    : hydrateFilteredItemsWithAds(filteredCards, waffleCards);

  const cardCount = cards.filter(({ type }) => type !== 'ad').length;

  const shouldDisplayMobileColumns = parseInt(mobileDisplay, 10) === 2;

  useEffect(() => {
    getPackageItemsTaxonomy(vertical, pageRoute, draftId)
      .then((updatedIdToTaxonomy) => {
        setIdToTaxonomy(updatedIdToTaxonomy);
      });
  }, [vertical, pageRoute, draftId]);

  useEffect(() => {
    const {
      categories: initialCategories,
      displayNameByKey: initialDisplayNameByKey,
      filterValues: initialFilterValues,
      categoryToDisplay: initialCategoryToDisplay,
    } = getInitialFilters(customFilters);

    setCategories(initialCategories);
    setDisplayNameByKey(initialDisplayNameByKey);
    setFilterValues(initialFilterValues);
    setCategoryToDisplay(initialCategoryToDisplay);
  }, [customFilters]);

  const handleFilterChange = useCallback((category) => (key, value) => {
    const newCategoryValues = getUpdatedCategoryValues(
      key,
      value,
      filterValues,
      category,
      enableMultiselectFilters,
    );

    // update filter values
    const newFilterValues = {
      ...filterValues,
      [category]: newCategoryValues,
    };

    const newActiveFilters = getNewActiveFilters({
      activeFilters,
      hasMultiSelect: enableMultiselectFilters,
      key,
      value,
      category,
      displayNameByKey,
      newCategoryValues,
    });

    setActiveFilters(newActiveFilters);
    setCategories({ ...categories });
    setFilterValues(newFilterValues);
  }, [
    activeFilters,
    categories,
    displayNameByKey,
    filterValues,
    subType,
    enableMultiselectFilters,
  ]);

  const clearAllFilters = useCallback(() => {
    const {
      categories: initialCategories,
      displayNameByKey: initialDisplayNameByKey,
      filterValues: initialFilterValues,
      categoryToDisplay: initialCategoryToDisplay,
    } = getInitialFilters(customFilters);

    setActiveFilters(new Map());
    setFilterValues(initialFilterValues);
    setCategories(initialCategories);
    setDisplayNameByKey(initialDisplayNameByKey);
    setCategoryToDisplay(initialCategoryToDisplay);
  }, [customFilters]);

  return (
    <div
      className={classNames(pkgClassName, styles.waffle)}
      data-packageid={id}
      data-testid="waffle"
      data-loaded={!!Object.keys(idToTaxonomy).length}
      ref={containerRef}
    >
      <Header
        animateBackground
        activeFilters={Array.from(activeFilters.values())}
        cardCount={cardCount}
        categoryToDisplay={categoryToDisplay}
        categories={categories}
        clearFilter={clearAllFilters}
        content={content}
        enableMultiselectFilters={enableMultiselectFilters}
        filterDisplayNameByKey={displayNameByKey}
        filterOnChange={handleFilterChange}
        filterValues={filterValues}
        isFilterActive={activeFilters.size > 0}
        showDisclaimer={showCommerceDisclaimer}
      />
      <List
        adsDisabled={adsDisabled}
        cards={cards}
        metadata={metadata}
        mobileDisplayColumns={shouldDisplayMobileColumns}
        vertical={vertical}
      />
    </div>
  );
};

const Waffle = connect(mapStateToProps)(DefaultWaffle);

export { Waffle };
